import axios, { AxiosResponse } from 'axios';

import CustomHttpClient from '../customHttpClient';

import { getError } from '../../utils/getError';
import userHelper from '../../utils/userHelper';

import {
  AccidentType,
  AccidentTypeList,
  Branch,
  BranchList,
  Cause,
  CivilStatus,
  Client,
  CollisionWith,
  CollisionWithList,
  Combo,
  CompanyCreditCards,
  CrystalListResponse,
  CrystalType,
  DECISION_TYPE,
  DocumentType,
  GetCauseListResponse,
  GetDocumentTypeListServiceResponse,
  GetLocalitiesListPerProvinceResponse,
  GetNoPrismPlacesList,
  GetPrismPlacesListResponse,
  GetProvinceListServiceResponse,
  GetRelationDriverListResponse,
  GetRelationShipCauseCoverageGeneratorRequest,
  GetRelationShipCauseCoverageGeneratorResponse,
  GetVehicleTypesListServiceResponse,
  GetWeatherStatesListResponse,
  IVATypeListServiceResponse,
  ListBranchEconomyActivity,
  ListCivilStatus,
  ListCountries,
  ListProfession,
  ListTypeSocietyService,
  MenuListServiceResponse,
  PendingInvoices,
  Producer,
  ProvidersList,
  SelectOption,
  State,
  Use,
  UserInformation,
  UseWebResponsive,
  VehicleType,
  WheelListResponse,
  WheelType,
  Years,
} from '../../interfaces';
import { Gender, GenderList } from '../../interfaces/services/gender-list-service';
import { NationalityList } from '../../interfaces/services/nationality-list-service';
import { CancellationReason, CancellationReasonResponse } from '../../interfaces/services/get-cancellation-reason-service';
import { getUserInfo } from '../../utils';
import { CancellationMethod, CancellationMethodResponse } from '../../interfaces/services/get-cancellation-method-service';
import { VehicleBrand } from '../../interfaces/services/get-vehicles-brands-response';
import { VehicleModel } from '../../interfaces/services/get-vehicle-models-response';
import { GlobalPublicConfigs } from '../../interfaces/services/global-config-service';
import { BankListResponse } from '../../interfaces/services/get-banks-list-service';
import { Company, CompanyListResponse } from '../../interfaces/services/get-company-list-service';
import { ServiceStatusResponse } from '../../interfaces/services/get-service-status-response';

const customHttpClientMasters = CustomHttpClient({ baseUrl: 'maestros' });
const customHttpClientMastersWithScope = CustomHttpClient({ baseUrl: 'maestros',scope: 'Maestros' });

const customHttpClientMastersClaimScope = CustomHttpClient({
  baseUrl: 'maestros',
  scope: 'Siniestros',
});

const customHttpClientMastersScope = CustomHttpClient({
  baseUrl: 'maestros',
  scope: 'Maestros',
});

const customHttpClient = CustomHttpClient({
  baseUrl: 'productores/cotizacion',
});

const customHttpClientProducers = CustomHttpClient({ baseUrl: 'productores' });

const S3_URL = process.env.REACT_APP_S3_URL;

const MasterService = {
  async getAllBranches(): Promise<AxiosResponse<BranchList>> {
    const url = 'listaRamas';

    try {
      const response = await customHttpClientMasters.get(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getBranchesForCombo(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaRamas';

    try {
      const response = await customHttpClientMasters.get(url);

      if (response.status === 200) {
        response.data = response.data.ramas.map((item: Branch) => {
          return {
            value: item.codigoRama,
            label: item.descripcionRama,
          };
        });
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
   async getInsuredForCombo(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaClientesCotizador';
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get(url, {
              params,
            });

      if (response.status === 200) {
        response.data = response.data.clientes.map((item: Client) => {
          return {
            value: item.codigo,
            label: item.nombre,
          };
        });
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async getStatesForCombo(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaEstadosDePoliza';

    try {
      const response = await customHttpClientMasters.get(url);

      if (response.status === 200) {
        response.data = response.data.estadosDePoliza.map((item: State) => {
          return {
            value: item.codigo,
            label: item.descripcion,
          };
        });
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getProducersForCombo(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'intermediariosPorCuit';

    try {
      const {
        codigoDeEmpresa,
        codigoDeSucursal,
        intermediarioPrincipal,
        cuit,
        usuarioInterno,
      }: UserInformation = userHelper.getUser()!;

      const params = {
        empresa: codigoDeEmpresa,
        sucursal: codigoDeSucursal,
        cuit,
        usri: usuarioInterno,
        niv1: intermediarioPrincipal?.nivelInter,
      };

      const response = await customHttpClientProducers.get(url, { params });

      if (response.status === 200) {
        response.data = response.data.intermediarios.intermediario.map(
          (item: Producer) => {
            return {
              value: item.codigoInter,
              label: `${item.codigoInter} - ${item.nombreInter}`,
            };
          }
        );
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getIntermediariesByIntermediaryCombo(): Promise<
    AxiosResponse<Array<SelectOption>>
  > {
    const url = 'intermediariosPorIntermediario';

    try {
      const user = userHelper.getUser();

      const params = {
        empresa: user?.codigoDeEmpresa,
        sucursal: user?.codigoDeSucursal,
        nivt: user?.intermediarioSecundario?.nivelInter,
        nivc: user?.intermediarioSecundario?.codigoInter,
      };
      const response = await customHttpClientProducers.get(url, { params });

      if (response.status === 200) {
        response.data = response.data.intermediarios.intermediario.map(
          (item: Producer) => {
            return {
              value: item.codigoInter,
              label: `${item.codigoInter} - ${item.nombreInter}`,
            };
          }
        );
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPendingInvoices({
    codeAux,
    numberAux,
    codMayorAux,
    nroMayorAux,
  }: {
    codeAux?: string;
    numberAux?: string;
    codMayorAux?: string;
    nroMayorAux?: string;
  }): Promise<AxiosResponse<Array<PendingInvoices>>> {
    const url = 'facturasPendientes';

    try {
      const { codigoDeEmpresa, codigoDeSucursal }: UserInformation =
        userHelper.getUser()!;

      const params = {
        empresa: codigoDeEmpresa,
        sucursal: codigoDeSucursal,
        codigoMayorAuxiliar: codMayorAux || codeAux,
        numeroMayorAuxiliar: nroMayorAux || numberAux,
      };

      const response = await customHttpClientProducers.get(url, { params });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListMenus(): Promise<AxiosResponse<MenuListServiceResponse, any>> {
    const url = 'listaMenues';

    try {
      const response =
        await customHttpClientMasters.get<MenuListServiceResponse>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getNivelIntermediarioForCombo(): Promise<
    AxiosResponse<Array<SelectOption>, any>
  > {
    //TODO: Reemplazar cuando exista un endpoint
    const url = 'listaMenues';

    try {
      const response = await customHttpClientMasters.get<Array<SelectOption>>(
        url
      );

      //TODO: Remover cuando exista el endpoint...
      if (response.status === 200) {
        response.data = [
          {
            // Productor
            value: '1',
            label: '1',
          },
          // Existe, pero no esta siendo utilizado
          // {
          //     // ???
          //     value: '2',
          //     label: '2',
          // },
          {
            // Organizador
            value: '3',
            label: '3',
          },
          {
            // Broker
            value: '5',
            label: '5',
          },
          {
            // Productor (3°)
            value: '6',
            label: '6',
          },
          // Existe, pero no esta siendo utilizado
          // {
          //     // ???
          //     value: '7',
          //     label: '7',
          // },
        ];
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getIVATypeList(): Promise<
    AxiosResponse<IVATypeListServiceResponse, any>
  > {
    const url = 'listaTiposDeIva';

    try {
      const response =
        await customHttpClientMasters.get<IVATypeListServiceResponse>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getLocalitiesListPerProvince(
    provinceCode: string
  ): Promise<AxiosResponse<GetLocalitiesListPerProvinceResponse, any>> {
    const url = `listaLocalidadesPorProvincia?codigoProvincia=${provinceCode}`;

    try {
      const response =
        await customHttpClientMasters.get<GetLocalitiesListPerProvinceResponse>(
          url
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getDocumentTypeList(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaTiposDeDocumento';

    try {
      const response =
        await customHttpClientMasters.get(
          url
        );
        if (response.status === 200) {
          response.data = response.data.documentos.map((document: DocumentType) => {
            return {
              value: String(document.codigo),
              label: document.descripcion,
            };
          });
        }
        return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getProvinceList(): Promise<
    AxiosResponse<GetProvinceListServiceResponse, any>
  > {
    const url = 'listaProvincias';

    try {
      const response =
        await customHttpClientMasters.get<GetProvinceListServiceResponse>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getGenderList(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaSexos';

    try {
      const response = await customHttpClientMasters.get(url);

      if (response.status === 200) {
        response.data = response.data.sexos.map((gender: Gender) => {
          return {
            value: gender.codigoSexo,
            label: gender.descripcionSexo,
          };
        });
      }

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getNationalityList(): Promise<AxiosResponse<NationalityList, any>> {
    const url = 'listaNacionalidades';

    try {
      const response = await customHttpClientMasters.get<NationalityList>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListTypesOfSociety(): Promise<
    AxiosResponse<ListTypeSocietyService, any>
  > {
    const url = 'tiposDeSociedad';

    try {
      const response =
        await customHttpClientMasters.get<ListTypeSocietyService>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListCountries(): Promise<AxiosResponse<ListCountries, any>> {
    const url = 'listaPaises';

    try {
      const response = await customHttpClientMasters.get<ListCountries>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListProfessions(): Promise<AxiosResponse<ListProfession, any>> {
    const url = 'listaProfesiones';

    try {
      const response = await customHttpClientMasters.get<ListProfession>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListCivilStatus(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaEstadoCivil';

    try {
      const response = await customHttpClientMasters.get(url);

      if (response.status === 200) {
        response.data = response.data.estadosCiviles.map((civilState: CivilStatus) => {
          return {
            value: civilState.codigoEstadoCivil,
            label: civilState.descripcionEstadoCivil,
          };
        });
      }
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListBranchEconomyActivity(): Promise<
    AxiosResponse<ListBranchEconomyActivity, any>
  > {
    const url = 'listaRamasActividadEconomica';

    try {
      const response =
        await customHttpClientMasters.get<ListBranchEconomyActivity>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListCreditCards(): Promise<AxiosResponse<CompanyCreditCards, any>> {
    const url = 'listaTarjetasDeCredito';

    try {
      const response = await customHttpClientMasters.get<CompanyCreditCards>(
        url
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getUseList(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaUsosWeb';

    try {
      const response = await customHttpClientMasters.get(url);
      if (response.status === 200) {
        response.data = response.data.usos.map((item: Use) => {
          return {
            value: item.codigoDeUsoVehiculo,
            label: item.descripcionUsoVehiculo,
          };
        });
      }
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getYearList(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'obtenerAnios';
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClient.get(url, {params});
      if (response.status === 200) {
        response.data = response.data.anios.map((item: Years) => {
          return {
            value: item.anio,
            label: item.anio,
          };
        });
      }
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getVehicleTypesList(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaTiposVehiculo';

    try {
      const response = await customHttpClientMastersClaimScope.get(url);
      if (response.status === 200) {
        response.data = response.data.tiposDeVehiculos.map((item: VehicleType) => {
          return {
            value: item.tipoVehiculo,
            label: item.descripcionTipoVehiculo,
          };
        });
      }
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getVehicleBrandsList(ramaParam: string): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaMarcas'; 

    try {
      const params = {
        rama: ramaParam,
      }

      const response = await customHttpClientProducers.get(url, {params});

      if (response.status === 200) {
        response.data = response.data.marcasVehiculo.map((item: VehicleBrand) => {
          return {
            value: item.codigo,
            label: item.descripcion,
          };
        });
      }
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getVehicleModelsPerBrandList(codigoMarcaInfoautoParam: string): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'obtenerModelosPorMarcaDeInfoauto';

    try {
      const params = {
        codigoMarcaInfoauto: codigoMarcaInfoautoParam, 
      }

      const response = await customHttpClientProducers.get(url, {params});
      
      if (response.status === 200) {
        response.data = response.data.gruposVehiculo.map((item: VehicleModel) => {
          return {
            value: item.codigo,
            label: item.descripcion,
          };
        });
      }
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getGoodResultTypesList(): Promise<Array<SelectOption>> {
    try {
      const response: Array<SelectOption> = await Promise.resolve().then(() => [
        {
          label: 'Sin buen resultado',
          value: '0',
        },
        {
          label: '1 año sin siniestros',
          value: '1',
        },
        {
          label: '2 años sin siniestros',
          value: '2',
        },
        {
          label: '3 o más años sin siniestros',
          value: '3',
        },
      ]);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getAccidentTypeList(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaTipoAccidente';

    try {
      const response = await customHttpClientMastersClaimScope.get(url);
      if (response.status === 200) {
        response.data = response.data.accidentes.map((accidents: AccidentType) => {
          return {
            value: accidents.codigoAccidente,
            label: accidents.descripcionAccidente,
          };
        });
      }
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCollisionWithList(): Promise<AxiosResponse<Array<SelectOption>>> {
    const url = 'listaTipoColision';

    try {
      const response = await customHttpClientMastersClaimScope.get(url);
      if (response.status === 200) {
        response.data = response.data.colisiones.map((collisions: CollisionWith) => {
          return {
            value: collisions.codigoColision,
            label: collisions.descripcionColision,
          };
        });
      }
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getProvidersList(providerCode: string): Promise<AxiosResponse<ProvidersList, any>> {
    const url = `listaProveedoresMaestros`;
    const params = { tipo: providerCode };
    try {
      const response = await customHttpClientMastersClaimScope.get<ProvidersList>(url, {params});
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getWeatherStatesList(): Promise<
    AxiosResponse<GetWeatherStatesListResponse>
  > {
    const url = 'listaEstadosTiempo';

    try {
      const response =
        await customHttpClientMastersClaimScope.get<GetWeatherStatesListResponse>(
          url
        );

        return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getVehicleList(): Promise<AxiosResponse<any>> {
    const url = 'listaVehiculos';

    try {
      const response = await customHttpClientMastersClaimScope.get<any>(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getNoPrismPlacesList(): Promise<
    AxiosResponse<GetNoPrismPlacesList>
  > {
    const url = 'listaLugaresNoPrisma';

    try {
      const response =
        await customHttpClientMastersClaimScope.get<GetNoPrismPlacesList>(
          url
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCauseList(numberBranch: number): Promise<Combo[]> {
    const url = `listaCausas?rama=${numberBranch}`;

    try {
      const response =
        await customHttpClientMastersClaimScope.get<GetCauseListResponse>(url);

      const data = response.data.ramas[0].causas.filter((cause: Cause) => cause.esWeb === DECISION_TYPE.YES).map((cause: Cause) => {
        return {
          value: cause.codigoCausa,
          label: cause.descripcionCausa,
        };
      });

      return data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPrismPlacesList(): Promise<
    AxiosResponse<GetPrismPlacesListResponse>
  > {
    const url = 'listaLugaresPrisma';

    try {
      const response =
        await customHttpClientMastersClaimScope.get<GetPrismPlacesListResponse>(
          url
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListCrystalTypes(): Promise<Combo[]> {
    const url = 'listaTiposCristales';

    try {
      const response =
        await customHttpClientMastersClaimScope.get<CrystalListResponse>(url);

      const data = response.data.tiposDeCristal.map((cause: CrystalType) => {
        return {
          value: cause.codigo,
          label: cause.descripcion,
        };
      });

      return data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getRelationDriverList(): Promise<
    AxiosResponse<GetRelationDriverListResponse>
  > {
    const url = 'listaRelacionAsegurado';

    try {
      const response =
        await customHttpClientMastersClaimScope.get<GetRelationDriverListResponse>(
          url
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListWheelTypes(): Promise<Combo[]> {
    const url = 'listaTiposRueda';

    try {
      const response =
        await customHttpClientMastersClaimScope.get<WheelListResponse>(url);

      const data = response.data.tiposDeRueda.map((cause: WheelType) => {
        return {
          value: cause.codigo,
          label: cause.descripcion,
        };
      });

      return data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getRelationShipCauseCoverageGenerator(
    data: GetRelationShipCauseCoverageGeneratorRequest
  ): Promise<GetRelationShipCauseCoverageGeneratorResponse> {
    const url = 'relacionCausaCoberturaHechoGenerador';
    const params = { ...data };

    try {
      const response = await customHttpClientMastersClaimScope.get(url, {
        params,
      });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async getListCancellationReason(article: string, user: string): Promise<Combo[]> {
    const url = 'listaTiposDeAnulacion';
    const params = {
      ...getUserInfo(),
      articulo: article,
      usuario: user,
    };
  
    try {
      const response = await customHttpClientMasters.get<CancellationReasonResponse>(url, { params });
  
      if (!response.data.motivosDeAnulacion || response.data.motivosDeAnulacion.length === 0) {
        return [];
      }
  
      return response.data.motivosDeAnulacion.map((reason: CancellationReason) => ({
        value: reason.subtipo,
        label: reason.descripcion,
      }));
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async getListCancellationMethod(article : string, user: string, stou: string): Promise<CancellationMethod[]> {
    const url = 'listaFormasDeAnulacion';
    const params = {
      ...getUserInfo(),
      articulo: article,
      usuario: user,
      stou: stou,
    };

    try {
      const response =
        await customHttpClientMasters.get<CancellationMethodResponse>(url,
          { params });

      return response.data.formasDeAnular;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getGlobalConfigs(): Promise<AxiosResponse<GlobalPublicConfigs>> {
    const url = `${S3_URL}config.json`;

    try {
      const response = await axios.get(url);

      return response;
    } catch (error) {      
      throw new Error(getError(error));
    }
  },

  async getAllBanks(): Promise<AxiosResponse<BankListResponse>> {
    const url = 'listaBancos';

    try {
      const response = await customHttpClientMastersScope.get(url);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async getListCompanies(): Promise<Combo[]> {
    const url = 'listaCompanias';

    try {
      const response = await customHttpClientMastersWithScope.get<CompanyListResponse>(url);

      if (!response.data.companias || response.data.companias.length === 0) {
        return [];
      }

      return response.data.companias.map((company: Company) => ({
        value: company.nombreCompania,
        label: company.nombreCompania,
      }));
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async getServiceStatus(): Promise<boolean> {
    const url = 'serviceStatus';

    try {
      const response =
        await customHttpClientMastersScope.get<ServiceStatusResponse>(url);

      return response.data.serviceStatus.onLine;
    } catch (error) {
      // Si el servicio falla o no responde correctamente, considerar la web en mantenimiento
      return false;
    }
  },
};

export default MasterService;